// Keep these public presets in sync with the H5 API's theme parser.
const FONT_PRESETS = {
  system: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  sans_serif: 'Arial, "Helvetica Neue", Helvetica, sans-serif',
  serif: 'Georgia, "Times New Roman", serif',
}
const BUTTON_PRESETS = { subtle: '4px', rounded: '12px', pill: '999px' }
const hasOwn = (object, key) => Object.prototype.hasOwnProperty.call(object, key)

function normalizeColor(value) {
  if (
    typeof value !== 'string' ||
    (value.length !== 4 && value.length !== 7) ||
    !/^#(?:[\da-f]{3}|[\da-f]{6})$/i.test(value)
  ) return null
  const hex = value.slice(1).toLowerCase()
  return `#${hex.length === 3 ? hex.split('').map(char => char + char).join('') : hex}`
}

export function parseLivenessTheme(value) {
  // URL parameters are untrusted. Invalid fields fall back to the existing UI;
  // cosmetic configuration must never interrupt an active detection session.
  if (typeof value !== 'string' || value.length > 1024) return {}
  let theme
  try {
    theme = JSON.parse(value)
  } catch {
    return {}
  }
  if (!theme || typeof theme !== 'object' || Array.isArray(theme)) return {}
  const result = {}
  for (const field of ['primary_color', 'text_color']) {
    const color = normalizeColor(theme[field])
    if (color) result[field] = color
  }
  if (typeof theme.font_family === 'string' && hasOwn(FONT_PRESETS, theme.font_family)) {
    result.font_family = theme.font_family
  }
  if (typeof theme.button_style === 'string' && hasOwn(BUTTON_PRESETS, theme.button_style)) {
    result.button_style = theme.button_style
  }
  return result
}

function channels(hex) {
  return [1, 3, 5].map(offset => parseInt(hex.slice(offset, offset + 2), 16))
}

function mixColor(hex, target, ratio) {
  return `#${channels(hex).map(channel =>
    Math.round(channel + (target - channel) * ratio).toString(16).padStart(2, '0')
  ).join('')}`
}

function buttonTextColor(hex) {
  // Choose black/white for the button only. Never reject or adjust customer text colors.
  const linear = channels(hex).map(channel => {
    const value = channel / 255
    return value <= 0.04045 ? value / 12.92 : Math.pow((value + 0.055) / 1.055, 2.4)
  })
  const luminance = 0.2126 * linear[0] + 0.7152 * linear[1] + 0.0722 * linear[2]
  return luminance > 0.179 ? '#000000' : '#ffffff'
}

export function livenessThemeStyle(value) {
  const theme = parseLivenessTheme(value)
  const style = {}
  if (theme.primary_color) {
    const primary = theme.primary_color
    const states = {
      primary,
      'primary-hover': mixColor(primary, 255, 0.2),
      'primary-active': mixColor(primary, 0, 0.1),
      'primary-disabled': mixColor(primary, 255, 0.5),
    }
    Object.keys(states).forEach(state => {
      style[`--h5-${state}`] = states[state]
      style[`--h5-${state}-text`] = buttonTextColor(states[state])
    })
  }
  if (theme.text_color) {
    style['--h5-text-color'] = theme.text_color
    style['--h5-muted-text'] = mixColor(theme.text_color, 255, 0.25)
  }
  if (theme.font_family) style['--h5-font-family'] = FONT_PRESETS[theme.font_family]
  if (theme.button_style) style['--h5-button-radius'] = BUTTON_PRESETS[theme.button_style]
  return style
}
